import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchJobPhases,
  createJobPhase,
  updatePhaseServicesThunk,
  createTaskThunk,
  updateTaskApiThunk,
  deleteTaskApiThunk,
} from '@/redux/thunk/jobPhasesThunk';

// Types matching the job phases page interfaces
export interface Assigner {
  id: string;
  name: string;
  profile_image?: string | null;
  profile_pic?: string | null; // For backward compatibility with mock data
}

export interface Schedule {
  uuid: string;
  position: string;
  full_name: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  status: number;
  profile_image?: string | null;
  image_url?: string | null;
  user_id?: string;
  team_member_uuid?: string;
}

export interface Task {
  id: string;
  name?: string;
  description: string;
  status?: number; // 0: Pending, 1: In Progress, 2: Completed
  task_number?: string;
  assignedTo?: string;
  dueDate?: string;
  assigners: Assigner[];
  schedules?: Schedule[];
}

export interface Service {
  id: string;
  name: string;
  type: number; // 0 for SERVICE, 1 for PRODUCT
  checked?: boolean;
  disabled?: boolean;
  tasks: Task[];
}

export interface JobPhase {
  id: string;
  title: string;
  description?: string;
  checked?: boolean;
  manuallyChecked?: boolean;
  services?: Service[];
  level?: number;
  task?: {
    id: string;
    name: string;
    description?: string;
    assigners: Assigner[];
  };
  tasks?: Task;
  sections?: Section[];
  is_invoiced?: boolean;
}

export interface Section {
  id: string;
  title: string;
  description: string;
  section_type: number;
  checked?: boolean;
  disabled?: boolean;
  level: number;
  phases?: JobPhase[];
  products_services: Service[];
}

// API Response types
export interface JobPhasesApiResponse {
  status: string;
  data: {
    phases: JobPhase[];
    sections: Section[];
  };
}

export interface JobPhasesState {
  phases: JobPhase[]; // Primary array like your JSON
  sections: Section[]; // Secondary array
  selectedPhases: { [key: string]: JobPhase | null };
  collapsedSections: { [key: string]: boolean };
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  lastUpdated: string | null;
}

const initialState: JobPhasesState = {
  phases: [],
  sections: [],
  selectedPhases: {},
  collapsedSections: {},
  status: 'idle',
  error: null,
  lastUpdated: null,
};

// Data transformer function to convert API response to Redux state format
export const transformApiDataToReduxState = (
  apiResponse: JobPhasesApiResponse,
): { phases: JobPhase[]; sections: Section[] } => {
  const { phases, sections } = apiResponse.data;

  // Keep phases' own sections as provided by the API. Do NOT backfill
  // empty phase.sections with the global sections list; the UI relies on
  // an empty array to correctly render phases with no sections/items.
  return {
    phases,
    sections,
  };
};

const jobPhasesSlice = createSlice({
  name: 'jobPhases',
  initialState,
  reducers: {
    // Add new phase with sections from existing data
    addPhase: (state) => {
      const phaseNum = state.phases.length + 1;
      const timestamp = Date.now();

      // Use sections from the first existing phase as template, or fallback to sections array
      let sectionsTemplate: Section[] = [];

      if (state.phases.length > 0 && state.phases[0].sections) {
        // Clone sections from the first phase and reset their state
        sectionsTemplate = state.phases[0].sections.map((section) => ({
          ...section,
          id: `${section.id}-${timestamp}`,
          checked: false,
          products_services: section.products_services.map((service) => ({
            ...service,
            id: `${service.id}-${timestamp}`,
            checked: false,
            disabled: false,
            tasks: [], // Reset tasks for new phase
          })),
        }));
      } else if (state.sections.length > 0) {
        // Fallback to sections array if no phases exist
        sectionsTemplate = state.sections.map((section) => ({
          ...section,
          id: `${section.id}-${timestamp}`,
          checked: false,
          products_services: section.products_services.map((service) => ({
            ...service,
            id: `${service.id}-${timestamp}`,
            checked: false,
            disabled: false,
            tasks: [],
          })),
        }));
      }

      // Set up disabled states for services in the new phase (but don't auto-select them)
      sectionsTemplate.forEach((section, sectionIndex) => {
        section.products_services?.forEach((service, serviceIndex) => {
          // Extract base service ID by removing timestamp suffix if present
          const baseServiceId =
            service.id.includes('-') && service.id.split('-').length > 5
              ? service.id.substring(0, service.id.lastIndexOf('-'))
              : service.id;

          // Check if this service is selected in other phases to determine disabled state
          const isSelectedInOtherPhase = state.phases.some((otherPh) =>
            otherPh.sections?.some((otherSec) =>
              otherSec.products_services?.some((otherSvc) => {
                const otherSvcBaseId =
                  otherSvc.id.includes('-') && otherSvc.id.split('-').length > 5
                    ? otherSvc.id.substring(0, otherSvc.id.lastIndexOf('-'))
                    : otherSvc.id;
                return otherSvcBaseId === baseServiceId && otherSvc.checked;
              }),
            ),
          );

          // All services start unchecked, but may be disabled if selected in other phases
          service.checked = false;
          service.disabled = isSelectedInOtherPhase;
        });
      });

      // Update section checked status (all services start unchecked)
      sectionsTemplate.forEach((section) => {
        section.checked = false;

        // Initialize disabled state for this section
        const allServicesSelectedInOtherPhases =
          section.products_services?.every((service) => {
            const serviceBaseId =
              service.id.includes('-') && service.id.split('-').length > 5
                ? service.id.substring(0, service.id.lastIndexOf('-'))
                : service.id;

            // Check if this service is selected in any other phase
            return state.phases.some((otherPh) =>
              otherPh.sections?.some((otherSec) =>
                otherSec.products_services?.some((otherSvc) => {
                  const otherSvcBaseId =
                    otherSvc.id.includes('-') &&
                    otherSvc.id.split('-').length > 5
                      ? otherSvc.id.substring(0, otherSvc.id.lastIndexOf('-'))
                      : otherSvc.id;
                  return otherSvcBaseId === serviceBaseId && otherSvc.checked;
                }),
              ),
            );
          }) || false;

        section.disabled = allServicesSelectedInOtherPhases;
      });

      const newPhase: JobPhase = {
        id: `phase-${phaseNum}-${timestamp}`,
        title: `Phase ${phaseNum}`,
        description: `Phase ${phaseNum} description`,
        checked: false,
        level: 0,
        sections: sectionsTemplate,
        services: [],
      };

      // Update phase checked status based on sections
      const allSectionsChecked = newPhase.sections?.every(
        (section) => section.checked,
      );
      newPhase.checked = allSectionsChecked || false;

      state.phases.push(newPhase);
      state.selectedPhases[newPhase.id] = newPhase;
      state.lastUpdated = new Date().toISOString();
    },

    // Remove phase
    removePhase: (state, action: PayloadAction<string>) => {
      const phaseId = action.payload;
      state.phases = state.phases.filter((phase) => phase.id !== phaseId);
      delete state.selectedPhases[phaseId];
      delete state.collapsedSections[phaseId];

      // Recalculate disabled states for all remaining services
      state.phases.forEach((ph) => {
        ph.sections?.forEach((sec) => {
          sec.products_services?.forEach((svc) => {
            const svcBaseId =
              svc.id.includes('-') && svc.id.split('-').length > 5
                ? svc.id.substring(0, svc.id.lastIndexOf('-'))
                : svc.id;

            // Check if this service is selected in any other remaining phase
            const isSelectedInOtherPhase = state.phases.some(
              (otherPh) =>
                otherPh.id !== ph.id &&
                otherPh.sections?.some((otherSec) =>
                  otherSec.products_services?.some((otherSvc) => {
                    const otherSvcBaseId =
                      otherSvc.id.includes('-') &&
                      otherSvc.id.split('-').length > 5
                        ? otherSvc.id.substring(0, otherSvc.id.lastIndexOf('-'))
                        : otherSvc.id;
                    return otherSvcBaseId === svcBaseId && otherSvc.checked;
                  }),
                ),
            );

            // Disable service if it's selected in another phase and not selected in current phase
            svc.disabled = isSelectedInOtherPhase && !svc.checked;
          });

          // Update section disabled status based on all services being selected in other phases
          const allServicesSelectedInOtherPhases =
            sec.products_services?.every((service) => {
              const serviceBaseId =
                service.id.includes('-') && service.id.split('-').length > 5
                  ? service.id.substring(0, service.id.lastIndexOf('-'))
                  : service.id;

              // Check if this service is selected in any other phase
              return state.phases.some(
                (otherPh) =>
                  otherPh.id !== ph.id &&
                  otherPh.sections?.some((otherSec) =>
                    otherSec.products_services?.some((otherSvc) => {
                      const otherSvcBaseId =
                        otherSvc.id.includes('-') &&
                        otherSvc.id.split('-').length > 5
                          ? otherSvc.id.substring(
                              0,
                              otherSvc.id.lastIndexOf('-'),
                            )
                          : otherSvc.id;
                      return (
                        otherSvcBaseId === serviceBaseId && otherSvc.checked
                      );
                    }),
                  ),
              );
            }) || false;

          sec.disabled = allServicesSelectedInOtherPhases;
        });
      });

      state.lastUpdated = new Date().toISOString();
    },

    // Deselect section (uncheck section and all its services)
    deselectSection: (
      state,
      action: PayloadAction<{ phaseId: string; sectionId: string }>,
    ) => {
      const { phaseId, sectionId } = action.payload;

      // Find the section in the main sections array instead of phase.sections
      const section = state.sections.find((s) => s.id === sectionId);
      if (!section) return;

      // Uncheck the section
      section.checked = false;

      // Uncheck all services in this section
      section.products_services?.forEach((service) => {
        service.checked = false;
        service.disabled = false;
      });

      // Enable all services with same base ID in other phases
      section.products_services?.forEach((service) => {
        const baseServiceId =
          service.id.includes('-') && service.id.split('-').length > 5
            ? service.id.substring(0, service.id.lastIndexOf('-'))
            : service.id;

        // Enable this service in all other phases
        state.phases.forEach((ph) => {
          if (ph.id !== phaseId) {
            ph.sections?.forEach((sec) => {
              sec.products_services?.forEach((svc) => {
                const svcBaseId =
                  svc.id.includes('-') && svc.id.split('-').length > 5
                    ? svc.id.substring(0, svc.id.lastIndexOf('-'))
                    : svc.id;

                if (svcBaseId === baseServiceId) {
                  svc.disabled = false;
                }
              });
            });
          }
        });
      });

      // Update section disabled status for all sections after service updates
      state.phases.forEach((ph) => {
        ph.sections?.forEach((sec) => {
          const allServicesDisabled = sec.products_services?.every(
            (s) => s.disabled && !s.checked,
          );
          sec.disabled = allServicesDisabled || false;
        });
      });

      // Update phase checked status based on all sections
      const allSectionsChecked = state.sections.every((sec) => sec.checked);
      const phase = state.phases.find((p) => p.id === phaseId);
      if (phase) {
        phase.checked = allSectionsChecked;
        phase.manuallyChecked = allSectionsChecked;
      }

      state.lastUpdated = new Date().toISOString();
    },

    // Toggle phase collapse
    togglePhaseCollapse: (state, action: PayloadAction<string>) => {
      const phaseId = action.payload;
      state.collapsedSections[phaseId] = !state.collapsedSections[phaseId];
    },

    // Select phase
    selectPhase: (
      state,
      action: PayloadAction<{ phaseId: string; phase: JobPhase }>,
    ) => {
      const { phaseId, phase } = action.payload;
      state.selectedPhases[phaseId] = phase;
    },

    // Toggle phase checkbox
    togglePhase: (
      state,
      action: PayloadAction<{
        phaseId: string;
        checked: boolean;
      }>,
    ) => {
      const { phaseId, checked } = action.payload;

      const phase = state.phases.find((p) => p.id === phaseId);
      if (!phase) return;

      // Update phase and all its services
      phase.checked = checked;
      phase.manuallyChecked = checked;

      // Update services and sections in all sections of this phase
      phase.sections?.forEach((section) => {
        section.checked = checked;
        section.products_services?.forEach((service) => {
          service.checked = checked;
        });
      });

      // Update selected phase if this phase was selected
      if (state.selectedPhases[phaseId]?.id === phaseId) {
        state.selectedPhases[phaseId] = checked ? phase : null;
      }

      state.lastUpdated = new Date().toISOString();
    },

    // Toggle section checkbox (new action for section-level selection)
    toggleSection: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        checked: boolean;
      }>,
    ) => {
      const { phaseId, sectionId, checked } = action.payload;

      // Find the section in the main sections array instead of phase.sections
      const section = state.sections.find((s) => s.id === sectionId);
      if (!section) return;

      // Don't allow toggling if section is disabled (all services selected in other phases)
      if (section.disabled) return;

      // Update section checked status
      section.checked = checked;

      // Update all services in this specific section
      section.products_services?.forEach((service) => {
        service.checked = checked;
      });

      // If section is being checked, disable all services with same base ID in other phases
      if (checked) {
        section.products_services?.forEach((service) => {
          const baseServiceId =
            service.id.includes('-') && service.id.split('-').length > 5
              ? service.id.substring(0, service.id.lastIndexOf('-'))
              : service.id;

          // Disable this service in all other phases
          state.phases.forEach((ph) => {
            if (ph.id !== phaseId) {
              ph.sections?.forEach((sec) => {
                sec.products_services?.forEach((svc) => {
                  const svcBaseId =
                    svc.id.includes('-') && svc.id.split('-').length > 5
                      ? svc.id.substring(0, svc.id.lastIndexOf('-'))
                      : svc.id;

                  if (svcBaseId === baseServiceId && !svc.checked) {
                    svc.disabled = true;
                  }
                });
              });
            }
          });
        });
      } else {
        // If section is being unchecked, enable all services with same base ID in other phases
        section.products_services?.forEach((service) => {
          const baseServiceId =
            service.id.includes('-') && service.id.split('-').length > 5
              ? service.id.substring(0, service.id.lastIndexOf('-'))
              : service.id;

          // Enable this service in all other phases
          state.phases.forEach((ph) => {
            if (ph.id !== phaseId) {
              ph.sections?.forEach((sec) => {
                sec.products_services?.forEach((svc) => {
                  const svcBaseId =
                    svc.id.includes('-') && svc.id.split('-').length > 5
                      ? svc.id.substring(0, svc.id.lastIndexOf('-'))
                      : svc.id;

                  if (svcBaseId === baseServiceId) {
                    svc.disabled = false;
                  }
                });
              });
            }
          });
        });
      }

      // Update section disabled status for all sections after service updates
      state.phases.forEach((ph) => {
        ph.sections?.forEach((sec) => {
          const allServicesDisabled = sec.products_services?.every(
            (s) => s.disabled && !s.checked,
          );
          const oldDisabled = sec.disabled;
          sec.disabled = allServicesDisabled || false;
        });
      });

      // Update phase checked status based on all sections
      const allSectionsChecked = state.sections.every((sec) => sec.checked);
      const phase = state.phases.find((p) => p.id === phaseId);
      if (phase) {
        phase.checked = allSectionsChecked;
        phase.manuallyChecked = allSectionsChecked;
      }

      state.lastUpdated = new Date().toISOString();
    },

    // Toggle service checkbox
    toggleService: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        serviceId: string;
        checked: boolean;
      }>,
    ) => {
      const { phaseId, sectionId, serviceId, checked } = action.payload;

      // Find the service in the main sections array instead of phase.sections
      const section = state.sections.find((s) => s.id === sectionId);
      if (!section) return;

      const service = section.products_services?.find(
        (s) => s.id === serviceId,
      );
      if (!service) return;

      // Get the base service ID (without timestamp) to check across phases
      const baseServiceId =
        serviceId.includes('-') && serviceId.split('-').length > 5
          ? serviceId.substring(0, serviceId.lastIndexOf('-'))
          : serviceId;

      // Check if service is already checked in another phase (using base ID)
      const checkedInOtherPhase = state.phases.some(
        (ph) =>
          ph.id !== phaseId &&
          ph.sections?.some((sec) =>
            sec.products_services?.some((svc) => {
              const svcBaseId =
                svc.id.includes('-') && svc.id.split('-').length > 5
                  ? svc.id.substring(0, svc.id.lastIndexOf('-'))
                  : svc.id;
              return svcBaseId === baseServiceId && svc.checked;
            }),
          ),
      );

      // Don't allow checking if service is already checked in another phase
      if (checked && checkedInOtherPhase) {
        return;
      }

      // Update service
      service.checked = checked;

      // Check if all available (non-disabled) services in this section are now selected
      const availableServices =
        section.products_services?.filter((s) => !s.disabled) || [];
      const allAvailableServicesChecked =
        availableServices.length > 0 &&
        availableServices.every((s) => s.checked);

      // Check if all services in this section are selected in other phases
      const allServicesSelectedInOtherPhases =
        section.products_services?.every((service) => {
          // Extract base ID: for timestamped IDs, remove the timestamp suffix
          // For regular UUIDs, use the full ID
          const serviceBaseId =
            service.id.includes('-') && service.id.split('-').length > 5
              ? service.id.substring(0, service.id.lastIndexOf('-'))
              : service.id;

          // Check if this service is selected in any other phase
          const isSelectedInOtherPhase = state.phases.some(
            (ph) =>
              ph.id !== phaseId &&
              ph.sections?.some((sec) =>
                sec.products_services?.some((svc) => {
                  const svcBaseId =
                    svc.id.includes('-') && svc.id.split('-').length > 5
                      ? svc.id.substring(0, svc.id.lastIndexOf('-'))
                      : svc.id;
                  return svcBaseId === serviceBaseId && svc.checked;
                }),
              ),
          );

          return isSelectedInOtherPhase;
        }) || false;

      // Auto-check the section if all available services are selected
      if (allAvailableServicesChecked) {
        section.checked = true;
      } else {
        // Uncheck section if not all available services are selected
        section.checked = false;
      }

      // Disable section if all services are selected in other phases
      section.disabled = allServicesSelectedInOtherPhases;

      // Update phase checked status based on all sections in the main sections array
      const allSectionsChecked = state.sections.every((sec) => sec.checked);
      const phase = state.phases.find((p) => p.id === phaseId);
      if (phase) {
        phase.checked = allSectionsChecked;
        phase.manuallyChecked = allSectionsChecked;
      }

      // Update disabled status for all services across all phases
      state.phases.forEach((ph) => {
        ph.sections?.forEach((sec) => {
          sec.products_services?.forEach((svc) => {
            const svcBaseId =
              svc.id.includes('-') && svc.id.split('-').length > 5
                ? svc.id.substring(0, svc.id.lastIndexOf('-'))
                : svc.id;

            // Check if this service is selected in any other phase
            const isSelectedInOtherPhase = state.phases.some(
              (otherPh) =>
                otherPh.id !== ph.id &&
                otherPh.sections?.some((otherSec) =>
                  otherSec.products_services?.some((otherSvc) => {
                    const otherSvcBaseId =
                      otherSvc.id.includes('-') &&
                      otherSvc.id.split('-').length > 5
                        ? otherSvc.id.substring(0, otherSvc.id.lastIndexOf('-'))
                        : otherSvc.id;
                    return otherSvcBaseId === svcBaseId && otherSvc.checked;
                  }),
                ),
            );

            // Disable service if it's selected in another phase and not selected in current phase
            svc.disabled = isSelectedInOtherPhase && !svc.checked;
          });

          // Update section disabled status based on all services being selected in other phases
          const allServicesSelectedInOtherPhases =
            sec.products_services?.every((service) => {
              const serviceBaseId =
                service.id.includes('-') && service.id.split('-').length > 5
                  ? service.id.substring(0, service.id.lastIndexOf('-'))
                  : service.id;

              // Check if this service is selected in any other phase
              return state.phases.some(
                (otherPh) =>
                  otherPh.id !== ph.id &&
                  otherPh.sections?.some((otherSec) =>
                    otherSec.products_services?.some((otherSvc) => {
                      const otherSvcBaseId =
                        otherSvc.id.includes('-') &&
                        otherSvc.id.split('-').length > 5
                          ? otherSvc.id.substring(
                              0,
                              otherSvc.id.lastIndexOf('-'),
                            )
                          : otherSvc.id;
                      return (
                        otherSvcBaseId === serviceBaseId && otherSvc.checked
                      );
                    }),
                  ),
              );
            }) || false;

          sec.disabled = allServicesSelectedInOtherPhases;

          // Update section checked status based on available services
          const availableServices =
            sec.products_services?.filter((s) => !s.disabled) || [];
          const allAvailableServicesChecked =
            availableServices.length > 0 &&
            availableServices.every((s) => s.checked);
          sec.checked = allAvailableServicesChecked;
        });
      });

      state.lastUpdated = new Date().toISOString();
    },

    // Update phase description
    updatePhaseDescription: (
      state,
      action: PayloadAction<{
        phaseId: string;
        description: string;
      }>,
    ) => {
      const { phaseId, description } = action.payload;

      const phase = state.phases.find((p) => p.id === phaseId);
      if (!phase) return;

      phase.description = description;
      state.lastUpdated = new Date().toISOString();
    },

    // Add task to service
    addTask: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        serviceId: string;
        taskName: string;
        description?: string;
      }>,
    ) => {
      const { phaseId, sectionId, serviceId, taskName, description } =
        action.payload;

      // Find the section in the main sections array instead of phase.sections
      const section = state.sections.find((s) => s.id === sectionId);
      if (!section) return;

      const service = section.products_services?.find(
        (s) => s.id === serviceId,
      );
      if (!service) return;

      // Generate task ID
      const taskNumber = service.tasks.length + 1;
      const taskId = `T${taskNumber.toString().padStart(3, '0')}`;

      const newTask: Task = {
        id: taskId,
        name: taskName,
        description: description || '',
        status: 0, // 0: Pending
        assignedTo: '',
        dueDate: '',
        assigners: [],
      };

      service.tasks.push(newTask);
      state.lastUpdated = new Date().toISOString();
    },

    // Update task
    updateTask: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        serviceId: string;
        taskId: string;
        taskName: string;
        description?: string;
      }>,
    ) => {
      const { phaseId, sectionId, serviceId, taskId, taskName, description } =
        action.payload;

      // Find the section in the main sections array instead of phase.sections
      const section = state.sections.find((s) => s.id === sectionId);
      if (!section) return;

      const service = section.products_services?.find(
        (s) => s.id === serviceId,
      );
      if (!service) return;

      const task = service.tasks.find((t) => t.id === taskId);
      if (!task) return;

      task.name = taskName;
      if (description !== undefined) {
        task.description = description;
      }

      state.lastUpdated = new Date().toISOString();
    },

    // Delete task
    deleteTask: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        serviceId: string;
        taskId: string;
      }>,
    ) => {
      const { phaseId, sectionId, serviceId, taskId } = action.payload;

      // Find the section in the main sections array instead of phase.sections
      const section = state.sections.find((s) => s.id === sectionId);
      if (!section) return;

      const service = section.products_services?.find(
        (s) => s.id === serviceId,
      );
      if (!service) return;

      service.tasks = service.tasks.filter((t) => t.id !== taskId);
      state.lastUpdated = new Date().toISOString();
    },

    // Update task status
    updateTaskStatus: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        serviceId: string;
        taskId: string;
        status: number; // 0: Pending, 1: In Progress, 2: Completed
      }>,
    ) => {
      const { phaseId, sectionId, serviceId, taskId, status } = action.payload;

      // Find the section in the main sections array instead of phase.sections
      const section = state.sections.find((s) => s.id === sectionId);
      if (!section) return;

      const service = section.products_services?.find(
        (s) => s.id === serviceId,
      );
      if (!service) return;

      const task = service.tasks.find((t) => t.id === taskId);
      if (!task) return;

      task.status = status;
      state.lastUpdated = new Date().toISOString();
    },

    // Remove service from phase (uncheck)
    removeService: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        serviceId: string;
      }>,
    ) => {
      const { phaseId, sectionId, serviceId } = action.payload;

      // Find the section in the main sections array instead of phase.sections
      const section = state.sections.find((s) => s.id === sectionId);
      if (!section) return;

      const service = section.products_services?.find(
        (s) => s.id === serviceId,
      );
      if (!service) return;

      // Uncheck the service
      service.checked = false;

      // Update section checked status based on all services in this section
      const allServicesInSectionChecked = section.products_services?.every(
        (s) => s.checked,
      );
      section.checked = allServicesInSectionChecked;

      // Update phase checked status based on all sections
      const allSectionsChecked = state.sections.every((sec) => sec.checked);
      const phase = state.phases.find((p) => p.id === phaseId);
      if (phase) {
        phase.checked = allSectionsChecked;
        phase.manuallyChecked = allSectionsChecked;
      }

      // Update disabled status for all services with same base ID across all phases
      const baseServiceId =
        serviceId.includes('-') && serviceId.split('-').length > 5
          ? serviceId.substring(0, serviceId.lastIndexOf('-'))
          : serviceId;

      state.phases.forEach((ph) => {
        ph.sections?.forEach((sec) => {
          sec.products_services?.forEach((svc) => {
            const svcBaseId =
              svc.id.includes('-') && svc.id.split('-').length > 5
                ? svc.id.substring(0, svc.id.lastIndexOf('-'))
                : svc.id;

            if (svcBaseId === baseServiceId) {
              // Check if this service is still selected in any other phase
              const isSelectedInOtherPhase = state.phases.some(
                (otherPh) =>
                  otherPh.id !== ph.id &&
                  otherPh.sections?.some((otherSec) =>
                    otherSec.products_services?.some((otherSvc) => {
                      const otherSvcBaseId =
                        otherSvc.id.includes('-') &&
                        otherSvc.id.split('-').length > 5
                          ? otherSvc.id.substring(
                              0,
                              otherSvc.id.lastIndexOf('-'),
                            )
                          : otherSvc.id;
                      return otherSvcBaseId === svcBaseId && otherSvc.checked;
                    }),
                  ),
              );

              svc.disabled = isSelectedInOtherPhase && !svc.checked;
            }
          });
        });
      });

      // Update section disabled status for all sections after service updates
      state.phases.forEach((ph) => {
        ph.sections?.forEach((sec) => {
          const allServicesDisabled = sec.products_services?.every(
            (s) => s.disabled && !s.checked,
          );
          sec.disabled = allServicesDisabled || false;
        });
      });

      state.lastUpdated = new Date().toISOString();
    },

    // Delete phase (uncheck all services)
    deletePhase: (state, action: PayloadAction<{ phaseId: string }>) => {
      const { phaseId } = action.payload;

      const phase = state.phases.find((p) => p.id === phaseId);
      if (!phase) return;

      // Uncheck all services in all sections of this phase
      phase.sections?.forEach((section) => {
        section.products_services?.forEach((service) => {
          service.checked = false;
          service.disabled = false;
        });
      });

      phase.checked = false;
      phase.manuallyChecked = false;

      // Clear selected phase if this was selected
      if (state.selectedPhases[phaseId]?.id === phaseId) {
        state.selectedPhases[phaseId] = null;
      }

      // Recalculate disabled states for all services across all phases
      state.phases.forEach((ph) => {
        ph.sections?.forEach((sec) => {
          sec.products_services?.forEach((svc) => {
            const svcBaseId =
              svc.id.includes('-') && svc.id.split('-').length > 5
                ? svc.id.substring(0, svc.id.lastIndexOf('-'))
                : svc.id;

            // Check if this service is selected in any other phase
            const isSelectedInOtherPhase = state.phases.some(
              (otherPh) =>
                otherPh.id !== ph.id &&
                otherPh.sections?.some((otherSec) =>
                  otherSec.products_services?.some((otherSvc) => {
                    const otherSvcBaseId =
                      otherSvc.id.includes('-') &&
                      otherSvc.id.split('-').length > 5
                        ? otherSvc.id.substring(0, otherSvc.id.lastIndexOf('-'))
                        : otherSvc.id;
                    return otherSvcBaseId === svcBaseId && otherSvc.checked;
                  }),
                ),
            );

            // Disable service if it's selected in another phase and not selected in current phase
            svc.disabled = isSelectedInOtherPhase && !svc.checked;
          });

          // Update section disabled status based on all services being selected in other phases
          const allServicesSelectedInOtherPhases =
            sec.products_services?.every((service) => {
              const serviceBaseId =
                service.id.includes('-') && service.id.split('-').length > 5
                  ? service.id.substring(0, service.id.lastIndexOf('-'))
                  : service.id;

              // Check if this service is selected in any other phase
              return state.phases.some(
                (otherPh) =>
                  otherPh.id !== ph.id &&
                  otherPh.sections?.some((otherSec) =>
                    otherSec.products_services?.some((otherSvc) => {
                      const otherSvcBaseId =
                        otherSvc.id.includes('-') &&
                        otherSvc.id.split('-').length > 5
                          ? otherSvc.id.substring(
                              0,
                              otherSvc.id.lastIndexOf('-'),
                            )
                          : otherSvc.id;
                      return (
                        otherSvcBaseId === serviceBaseId && otherSvc.checked
                      );
                    }),
                  ),
              );
            }) || false;

          sec.disabled = allServicesSelectedInOtherPhases;
        });
      });

      state.lastUpdated = new Date().toISOString();
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.status = action.payload ? 'loading' : 'idle';
    },

    // Set error
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.status = action.payload ? 'failed' : 'succeeded';
    },

    // Clear all job phases data
    clearJobPhases: (state) => {
      state.phases = [];
      state.sections = [];
      state.selectedPhases = {};
      state.collapsedSections = {};
      state.status = 'idle';
      state.error = null;
      state.lastUpdated = null;
    },

    // Load job phases from API data
    loadJobPhases: (
      state,
      action: PayloadAction<{ phases: JobPhase[]; sections: Section[] }>,
    ) => {
      state.phases = action.payload.phases;
      state.sections = action.payload.sections;
      state.status = 'succeeded';
      state.error = null;
      state.lastUpdated = new Date().toISOString();
    },

    // Load job phases from API response format
    loadJobPhasesFromApiResponse: (
      state,
      action: PayloadAction<JobPhasesApiResponse>,
    ) => {
      const transformedData = transformApiDataToReduxState(action.payload);
      state.phases = transformedData.phases;
      state.sections = transformedData.sections;

      // Set first phase as selected
      state.selectedPhases = {};
      if (transformedData.phases.length > 0) {
        state.selectedPhases[transformedData.phases[0].id] =
          transformedData.phases[0];
      }

      state.status = 'succeeded';
      state.error = null;
      state.lastUpdated = new Date().toISOString();
    },

    // Load your specific JSON object as initial state
    loadYourJsonAsInitialState: (state) => {
      // Clear existing state first
      state.phases = [];
      state.sections = [];
      state.selectedPhases = {};
      state.collapsedSections = {};

      // Create phases that contain sections (matching your exact JSON structure)
      const phases: JobPhase[] = [
        {
          id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
          title: 'Phase one',
          task: {
            id: 'task one',
            name: 'task one of phase one',
            description: 'task one of phase one',
            assigners: [
              {
                profile_pic: '/abc.png',
                name: 'ABC',
                id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
              },
            ],
          },
          sections: [
            {
              id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
              title: 'General one',
              description: 'Default section for existing quote items',
              section_type: 1,
              checked: true,
              level: 1,
              products_services: [
                {
                  id: '753dcd87-5925-4dec-95f6-3a03ff8c2a4f',
                  name: 'Air Conditioner Installation',
                  type: 1,
                  checked: true,
                  disabled: false,
                  tasks: [
                    {
                      id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                      name: 'task air condition fix',
                      description: 'task air condition fix',
                      assigners: [
                        {
                          profile_pic: '/xyz.png',
                          name: 'XYZ',
                          id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                        },
                      ],
                    },
                  ],
                },
                {
                  id: '7c3a5a2c-6f3a-4b7e-9fb7-3f6f0f1c2b10',
                  name: 'Electrical Wiring & Breaker Setup',
                  type: 1,
                  checked: true,
                  disabled: false,
                  tasks: [
                    {
                      id: '0b6c5d2e-2c1c-4e37-8a5b-1b9d2f4e7a90',
                      description:
                        'Install dedicated 20A breaker and run conduit',
                      assigners: [
                        {
                          profile_pic: '/abc.png',
                          name: 'ABC',
                          id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                        },
                      ],
                    },
                  ],
                },
                {
                  id: 'a1b2c3d4-55aa-47e2-9c11-0f0f0f0f0f01',
                  name: 'Copper Piping & Insulation Kit (up to 10m)',
                  type: 2,
                  checked: true,
                  disabled: false,
                  tasks: [],
                },
                {
                  id: 'bb22cc33-dd44-55ee-66ff-77889900aa11',
                  name: 'Smart Thermostat Supply (Nest)',
                  type: 2,
                  checked: true,
                  disabled: false,
                  tasks: [],
                },
                {
                  id: 'cc33dd44-ee55-66ff-77aa-88990011bb22',
                  name: 'Smart Thermostat Setup & Configuration',
                  type: 1,
                  checked: true,
                  disabled: false,
                  tasks: [
                    {
                      id: 'd4e5f6a7-b8c9-4012-9def-1234567890ab',
                      description: 'Mount, wire, and configure schedules',
                      assigners: [
                        {
                          profile_pic: '/xyz.png',
                          name: 'XYZ',
                          id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                        },
                      ],
                    },
                  ],
                },
                {
                  id: 'dd44ee55-ff66-77aa-88bb-99001122cc33',
                  name: 'AC Duct Cleaning & Sanitization',
                  type: 1,
                  checked: true,
                  disabled: false,
                  tasks: [
                    {
                      id: 'e6f7a8b9-c0d1-4e2f-9a9a-abcdefabcdef',
                      description:
                        'Vacuum ducts and apply antimicrobial treatment',
                      assigners: [
                        {
                          profile_pic: '/abc.png',
                          name: 'ABC',
                          id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              id: '2a3b4c5d-1111-2222-3333-444455556666',
              title: 'Electrical & Mounting',
              description: 'Mounting hardware and electrical hookups',
              section_type: 1,
              checked: false,
              level: 2,
              products_services: [
                {
                  id: '9f8e7d6c-aaaa-bbbb-cccc-ddddeeeefff1',
                  name: 'Wall Bracket & Vibration Pads',
                  type: 2,
                  checked: true,
                  disabled: false,
                  tasks: [],
                },
                {
                  id: '8e7d6c5b-b1c2-d3e4-f5a6-b7c8d9e0f111',
                  name: 'Outdoor Unit Mounting & Leveling',
                  type: 1,
                  checked: false,
                  disabled: false,
                  tasks: [
                    {
                      id: '1f2e3d4c-5b6a-7081-92a3-b4c5d6e7f8a9',
                      description: 'Anchor bracket, level unit, secure lines',
                      assigners: [
                        {
                          profile_pic: '/xyz.png',
                          name: 'XYZ',
                          id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                        },
                      ],
                    },
                  ],
                },
                {
                  id: '7d6c5b4a-2222-3333-4444-555566667777',
                  name: 'Power Cable & Isolator Switch',
                  type: 2,
                  checked: false,
                  disabled: false,
                  tasks: [],
                },
              ],
            },
            {
              id: '3b4c5d6e-7777-8888-9999-0000aaaabbbb',
              title: 'Cleanup & Handover',
              description: 'Final cleaning, disposal, and customer handover',
              section_type: 1,
              checked: false,
              level: 2,
              products_services: [
                {
                  id: '6c5b4a39-cccc-dddd-eeee-ffff00001111',
                  name: 'Site Cleanup & Waste Disposal',
                  type: 1,
                  checked: false,
                  disabled: false,
                  tasks: [
                    {
                      id: 'ab12cd34-ef56-7890-ab12-cd34ef567890',
                      description:
                        'Remove packing, vacuum workspace, dispose waste',
                      assigners: [
                        {
                          profile_pic: '/abc.png',
                          name: 'ABC',
                          id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                        },
                      ],
                    },
                  ],
                },
                {
                  id: '5b4a3928-eeee-ffff-0000-111122223333',
                  name: 'Customer Handover & Basic Training',
                  type: 1,
                  checked: false,
                  disabled: false,
                  tasks: [
                    {
                      id: 'cd34ef56-7890-ab12-cd34-ef567890ab12',
                      description:
                        'Demo operation modes, filters, and maintenance tips',
                      assigners: [
                        {
                          profile_pic: '/xyz.png',
                          name: 'XYZ',
                          id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ];

      // Create sections array (separate from phases, as in your JSON)
      const sections: Section[] = [
        {
          id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
          title: 'General one',
          description: 'Default section for existing quote items',
          section_type: 1,
          checked: false,
          level: 2,
          phases: phases,
          products_services: [
            {
              id: '753dcd87-5925-4dec-95f6-3a03ff8c2a4f',
              name: 'Air Conditioner Installation',
              type: 1,
              checked: true,
              disabled: false,
              tasks: [
                {
                  id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                  description: 'task air condition fix',
                  assigners: [
                    {
                      profile_pic: '/xyz.png',
                      name: 'XYZ',
                      id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                    },
                  ],
                },
              ],
            },
            {
              id: '7c3a5a2c-6f3a-4b7e-9fb7-3f6f0f1c2b10',
              name: 'Electrical Wiring & Breaker Setup',
              type: 1,
              checked: false,
              disabled: false,
              tasks: [
                {
                  id: '0b6c5d2e-2c1c-4e37-8a5b-1b9d2f4e7a90',
                  description: 'Install dedicated 20A breaker and run conduit',
                  assigners: [
                    {
                      profile_pic: '/abc.png',
                      name: 'ABC',
                      id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                    },
                  ],
                },
              ],
            },
            {
              id: 'a1b2c3d4-55aa-47e2-9c11-0f0f0f0f0f01',
              name: 'Copper Piping & Insulation Kit (up to 10m)',
              type: 2,
              checked: true,
              disabled: false,
              tasks: [],
            },
            {
              id: 'bb22cc33-dd44-55ee-66ff-77889900aa11',
              name: 'Smart Thermostat Supply (Nest)',
              type: 2,
              checked: false,
              disabled: false,
              tasks: [],
            },
            {
              id: 'cc33dd44-ee55-66ff-77aa-88990011bb22',
              name: 'Smart Thermostat Setup & Configuration',
              type: 1,
              checked: false,
              disabled: false,
              tasks: [
                {
                  id: 'd4e5f6a7-b8c9-4012-9def-1234567890ab',
                  description: 'Mount, wire, and configure schedules',
                  assigners: [
                    {
                      profile_pic: '/xyz.png',
                      name: 'XYZ',
                      id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                    },
                  ],
                },
              ],
            },
            {
              id: 'dd44ee55-ff66-77aa-88bb-99001122cc33',
              name: 'AC Duct Cleaning & Sanitization',
              type: 1,
              checked: false,
              disabled: false,
              tasks: [
                {
                  id: 'e6f7a8b9-c0d1-4e2f-9a9a-abcdefabcdef',
                  description: 'Vacuum ducts and apply antimicrobial treatment',
                  assigners: [
                    {
                      profile_pic: '/abc.png',
                      name: 'ABC',
                      id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                    },
                  ],
                },
              ],
            },
            {
              id: 'ee55ff66-77aa-88bb-99cc-00112233dd44',
              name: 'Annual Maintenance Plan (2 visits)',
              type: 1,
              checked: false,
              disabled: false,
              tasks: [
                {
                  id: 'fa12cb34-de56-4789-abcd-001122334455',
                  description:
                    'Biannual inspection, gas check, and coil cleaning',
                  assigners: [
                    {
                      profile_pic: '/xyz.png',
                      name: 'XYZ',
                      id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          id: '2a3b4c5d-1111-2222-3333-444455556666',
          title: 'Electrical & Mounting',
          description: 'Mounting hardware and electrical hookups',
          section_type: 1,
          checked: false,
          level: 2,
          phases: phases,
          products_services: [
            {
              id: '9f8e7d6c-aaaa-bbbb-cccc-ddddeeeefff1',
              name: 'Wall Bracket & Vibration Pads',
              type: 2,
              checked: false,
              disabled: false,
              tasks: [],
            },
            {
              id: '8e7d6c5b-b1c2-d3e4-f5a6-b7c8d9e0f111',
              name: 'Outdoor Unit Mounting & Leveling',
              type: 1,
              checked: false,
              disabled: false,
              tasks: [
                {
                  id: '1f2e3d4c-5b6a-7081-92a3-b4c5d6e7f8a9',
                  description: 'Anchor bracket, level unit, secure lines',
                  assigners: [
                    {
                      profile_pic: '/xyz.png',
                      name: 'XYZ',
                      id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                    },
                  ],
                },
              ],
            },
            {
              id: '7d6c5b4a-2222-3333-4444-555566667777',
              name: 'Power Cable & Isolator Switch',
              type: 2,
              checked: false,
              disabled: false,
              tasks: [],
            },
          ],
        },
        {
          id: '3b4c5d6e-7777-8888-9999-0000aaaabbbb',
          title: 'Cleanup & Handover',
          description: 'Final cleaning, disposal, and customer handover',
          section_type: 1,
          checked: false,
          level: 2,
          phases: phases,
          products_services: [
            {
              id: '6c5b4a39-cccc-dddd-eeee-ffff00001111',
              name: 'Site Cleanup & Waste Disposal',
              type: 1,
              checked: false,
              disabled: false,
              tasks: [
                {
                  id: 'ab12cd34-ef56-7890-ab12-cd34ef567890',
                  description:
                    'Remove packing, vacuum workspace, dispose waste',
                  assigners: [
                    {
                      profile_pic: '/abc.png',
                      name: 'ABC',
                      id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                    },
                  ],
                },
              ],
            },
            {
              id: '5b4a3928-eeee-ffff-0000-111122223333',
              name: 'Customer Handover & Basic Training',
              type: 1,
              checked: false,
              disabled: false,
              tasks: [
                {
                  id: 'cd34ef56-7890-ab12-cd34-ef567890ab12',
                  description:
                    'Demo operation modes, filters, and maintenance tips',
                  assigners: [
                    {
                      profile_pic: '/xyz.png',
                      name: 'XYZ',
                      id: 'e5e6cc8d-df0a-4277-88e1-fef67d635f34',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ];

      state.phases = phases;
      state.sections = sections;

      // Set first phase as selected
      state.selectedPhases = {};
      if (phases.length > 0) {
        state.selectedPhases[phases[0].id] = phases[0];
      }

      // Initialize disabled state for all sections
      state.phases.forEach((phase) => {
        phase.sections?.forEach((section) => {
          const allServicesSelectedInOtherPhases =
            section.products_services?.every((service) => {
              const serviceBaseId =
                service.id.includes('-') && service.id.split('-').length > 5
                  ? service.id.substring(0, service.id.lastIndexOf('-'))
                  : service.id;

              // Check if this service is selected in any other phase
              return state.phases.some(
                (otherPh) =>
                  otherPh.id !== phase.id &&
                  otherPh.sections?.some((otherSec) =>
                    otherSec.products_services?.some((otherSvc) => {
                      const otherSvcBaseId =
                        otherSvc.id.includes('-') &&
                        otherSvc.id.split('-').length > 5
                          ? otherSvc.id.substring(
                              0,
                              otherSvc.id.lastIndexOf('-'),
                            )
                          : otherSvc.id;
                      return (
                        otherSvcBaseId === serviceBaseId && otherSvc.checked
                      );
                    }),
                  ),
              );
            }) || false;

          section.disabled = allServicesSelectedInOtherPhases;
        });
      });

      state.status = 'succeeded';
      state.error = null;
      state.lastUpdated = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobPhases.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchJobPhases.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;

        // Transform API response to Redux state
        const transformedData = transformApiDataToReduxState(action.payload);
        state.phases = transformedData.phases;
        state.sections = transformedData.sections;

        // Set first phase as selected if phases exist
        state.selectedPhases = {};
        if (transformedData.phases.length > 0) {
          state.selectedPhases[transformedData.phases[0].id] =
            transformedData.phases[0];
        }

        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchJobPhases.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch job phases';
        console.error('Failed to fetch job phases:', action.payload);
      })
      .addCase(createJobPhase.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createJobPhase.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;

        // After creating a phase, refetch the phases to get updated data
        // This will be handled by the component calling fetchJobPhases again
      })
      .addCase(createJobPhase.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to create job phase';
        console.error('Failed to create job phase:', action.payload);
      })
      .addCase(updatePhaseServicesThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updatePhaseServicesThunk.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updatePhaseServicesThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to update phase services';
        console.error('Failed to update phase services:', action.payload);
      })
      .addCase(createTaskThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createTaskThunk.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.lastUpdated = new Date().toISOString();

        // The task is created via API and phases will be refreshed
        // The task_number is included in the API response and will be available
        // when phases are fetched again
      })
      .addCase(createTaskThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to create task';
        console.error('Failed to create task:', action.payload);
      })
      .addCase(updateTaskApiThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateTaskApiThunk.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateTaskApiThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to update task';
        console.error('Failed to update task:', action.payload);
      })
      .addCase(deleteTaskApiThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteTaskApiThunk.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.lastUpdated = new Date().toISOString();

        // The task is deleted via API and phases will be refreshed
        // The task_number is included in the API response and will be available
        // when phases are fetched again
      })
      .addCase(deleteTaskApiThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to delete task';
        console.error('Failed to delete task:', action.payload);
      });
  },
});

export const {
  addPhase,
  removePhase,
  deselectSection,
  togglePhaseCollapse,
  selectPhase,
  togglePhase,
  toggleSection,
  toggleService,
  updatePhaseDescription,
  addTask,
  updateTask,
  deleteTask,
  updateTaskStatus,
  removeService,
  deletePhase,
  setLoading,
  setError,
  clearJobPhases,
  loadJobPhases,
  loadJobPhasesFromApiResponse,
  loadYourJsonAsInitialState,
} = jobPhasesSlice.actions;

export default jobPhasesSlice.reducer;
